import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Avatar from "../../design-system/Avatar";
import { Card, CardHeader, CardBody } from "@material-tailwind/react";
import { loadNumberOfFiles } from "../../utils/appConstant";
import InfiniteScroll from "react-infinite-scroll-component";
import styles from "./ThoughtList.module.css";
import MultipleLoadingScreen from "../../design-system/MultipleLoadingScreen";
import { get_user_data, getInitials } from "../../utils/common";
import { getFileChats, getFiles } from "../../api/axiosApi";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { markdownComponents } from "../thoughts/chat/RenderResponse";
type Thought = {
    filename: string;
    timestamp: string;
    query: string;
    response: string;
    citations: string[];
};

type ThoughtsHeaderProps = {
    thought: Thought;
};
const ThoughtHeader = ({ thought }: ThoughtsHeaderProps) => {

    function getNumberOfDaysSinceTimestamp(timestamp: any): string {
        const inputDate = new Date(timestamp * 1);
        const currentDate = new Date();
        inputDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);
        currentDate.setDate(currentDate.getDate());

        const differenceInTime = currentDate.getTime() - inputDate.getTime();
        const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));
        let result = "";
        if (differenceInDays === 1 || differenceInDays === 0) {
            result += `${differenceInDays} day`;
        } else {
            result += `${differenceInDays} days`;
        }

        return result;
    }
    const initials = getInitials(get_user_data().name);

    return (
        <div className={`${styles.header} p-1 text-gray-700`}>
            <div className="flex items-center">
                <Avatar initials={initials} className="mr-2" />
            </div>
            <span className={`justify-self-center truncate w-full max-w-full text-gray-500`}>{getNumberOfDaysSinceTimestamp(thought.filename)}</span>
            <h2 className={`justify-self-center font-title truncate w-full max-w-full`}>{thought.query}</h2>
        </div>
    );
};

export const ThoughtList = () => {
    const [individualThought, setIndividualThought] = useState<Thought[]>([]);
    const [, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const navigate = useNavigate();
    let fileIndex = 0;
    const fetchAndSetThoughts = async () => {
        setIsLoading(true);
        try {
            if (get_user_data() !== null && get_user_data() !== undefined) {
                const thoughtAllFiles = await getFiles();
                const files = await thoughtAllFiles.data;
                files.sort((a: any, b: any) => {
                    const timestampA = Number(a.slice(0, -4));
                    const timestampB = Number(b.slice(0, -4));
                    return timestampB - timestampA;
                });
                let tempIndividualThought: Thought[] = [];
                for (let i = 0; i < Math.min(files.length, loadNumberOfFiles) && fileIndex < files.length; i++) {
                    const historyResponse = await getFileChats(files[fileIndex].substring(0, files[fileIndex].lastIndexOf(".")));
                    if (!historyResponse) {
                        fileIndex++;
                        continue;
                    }
                    const data = await historyResponse.data;
                    const lastObject = data[0];
                    lastObject["filename"] = files[fileIndex].substring(0, files[fileIndex].lastIndexOf("."));
                    tempIndividualThought.push(lastObject);
                    fileIndex++;
                }
                setIndividualThought(prevThoughts => [...prevThoughts, ...tempIndividualThought]);
                if (files.length === fileIndex) {
                    setHasMore(false);
                    return;
                }
            } else {
                navigate("/user_login");
            }
        } catch (error) {
            setIsLoading(false);
            setHasMore(false);

            console.error(`Failed to fetch thought files `, error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchMoreThoughts = useCallback(() => {
        if (hasMore) {
            fetchAndSetThoughts();
        }
    }, [hasMore]);

    useEffect(() => {
        fetchAndSetThoughts();
    }, []);

    return (
        <div className="flex-grow pb-8 relative">
            <InfiniteScroll
                dataLength={individualThought.length}
                next={fetchMoreThoughts}
                hasMore={hasMore}
                loader={
                    <div className="p-4">
                        <MultipleLoadingScreen count={3} />
                    </div>
                }
            >
                {individualThought.map((item: Thought, index) => {
                    item.response = item.response.replace(/\[[^\]]*\]/g, "");
                    const openSpecificThoughtClick = (item: any) => {
                        navigate(`/interaction/${item.filename}`, { state: { from: "individual" } });
                    };

                    return (
                        <Card onClick={() => openSpecificThoughtClick(item)} key={index} className="mt-6 hover:cursor-pointer  pb-4">
                            <CardHeader className="relative ">
                                <ThoughtHeader thought={item} />
                            </CardHeader>
                            <CardBody className="p-2 overflow-auto  max-h-[300px] ">
                                <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                    {item.response}
                                </ReactMarkdown>
                            </CardBody>
                        </Card>
                    );
                })}
            </InfiniteScroll>
        </div>
    );
};

export default ThoughtList;
